/**
 * Kom igång answers on the teacher overview, as class totals only (slice C, ruling 8). The
 * server sends no per-student answers at all and withholds the breakdown below `ONBOARDING_MIN_GROUP` saved
 * answers; this card only prints what it gets.
 */
import {
  COURSE_POSITIONS,
  MATH_CONFIDENCE,
  MINUTES_PER_DAY,
  ONBOARDING_MIN_GROUP,
  STUDY_GOALS,
} from "@shared/userPrefs";
import { Card } from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { formatNumber } from "@client/lib/format";
import type { OnboardingCounts } from "./types";

export function OnboardingTotals({ counts }: { counts: OnboardingCounts }) {
  const t = useT();
  const { lang } = useLang();
  const n = (value: number) => formatNumber(value, lang);
  const { breakdown } = counts;

  const groups = breakdown
    ? [
        {
          title: t("teacher.onboarding.position"),
          rows: COURSE_POSITIONS.map(
            (v) => [t(`onboarding.position.${v}`), breakdown.position[v]] as const,
          ),
        },
        {
          title: t("teacher.onboarding.goal"),
          rows: STUDY_GOALS.map((v) => [t(`onboarding.goal.${v}`), breakdown.goal[v]] as const),
        },
        {
          title: t("teacher.onboarding.minutes"),
          rows: MINUTES_PER_DAY.map(
            (v) =>
              [t(`onboarding.minutes.${v}`), breakdown.minutesPerDay[String(v) as "10"]] as const,
          ),
        },
        {
          title: t("teacher.onboarding.math"),
          rows: MATH_CONFIDENCE.map(
            (v) => [t(`onboarding.math.${v}`), breakdown.mathConfidence[v]] as const,
          ),
        },
      ]
    : [];

  return (
    <section className="mt-8" aria-labelledby="teacher-onboarding-title">
      <Card
        compact
        title={<span id="teacher-onboarding-title">{t("teacher.onboarding.title")}</span>}
        description={t("teacher.onboarding.privacy")}
      >
        <p className="text-sm text-ink-700 dark:text-ink-300">
          {t("teacher.onboarding.summary", {
            answered: n(counts.answered),
            skipped: n(counts.skipped),
            notYet: n(counts.notYet),
          })}
        </p>
        {breakdown ? (
          <div className="mt-4 grid gap-4 text-xs sm:grid-cols-4">
            {groups.map((group) => (
              <dl key={group.title}>
                <dt className="mb-1 font-medium text-ink-700 dark:text-ink-300">{group.title}</dt>
                {group.rows.map(([label, value]) => (
                  <dd
                    key={label}
                    className="flex justify-between gap-2 tabular-nums text-ink-600 dark:text-ink-400"
                  >
                    <span>{label}</span>
                    <span>{n(value)}</span>
                  </dd>
                ))}
              </dl>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">
            {t("teacher.onboarding.tooFew", { min: n(ONBOARDING_MIN_GROUP) })}
          </p>
        )}
      </Card>
    </section>
  );
}
